/**
 * Reports, and the coverage figures their cover page states.
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { z } from 'zod';
import * as reportsApi from '../api/Reports';
import { parsed } from '../api/parse';
import {
	auditCoverageSchema,
	reportSchema,
	type ReportStatus,
} from '../schemas';
import { keys } from './keys';

export function useAuditReportsQuery(auditId: string) {
	return useQuery({
		queryKey: keys.audits.reports(auditId),
		queryFn: async () =>
			parsed(
				z.array(reportSchema),
				await reportsApi.getAuditReports(auditId),
				`/audits/${auditId}/reports`,
			),
	});
}

export function useAuditCoverageQuery(auditId: string) {
	return useQuery({
		queryKey: keys.audits.coverage(auditId),
		queryFn: async () =>
			parsed(
				auditCoverageSchema,
				await reportsApi.getAuditCoverage(auditId),
				`/audits/${auditId}/coverage`,
			),
	});
}

/**
 * Generating and transitioning both move the audit itself — a first report
 * sends it to UNDER_REVIEW, and issuing closes it — so both invalidate the
 * audit as well as its report list.
 */
function invalidate(
	client: ReturnType<typeof useQueryClient>,
	auditId: string,
) {
	void client.invalidateQueries({ queryKey: keys.audits.reports(auditId) });
	void client.invalidateQueries({ queryKey: keys.audits.detail(auditId) });
}

export function useGenerateReportMutation(auditId: string) {
	const client = useQueryClient();
	return useMutation({
		mutationFn: async () =>
			parsed(
				reportSchema,
				await reportsApi.generateReport(auditId),
				`/audits/${auditId}/reports`,
			),
		onSuccess: () => invalidate(client, auditId),
	});
}

export function useSetReportStatusMutation(auditId: string) {
	const client = useQueryClient();
	return useMutation({
		mutationFn: async (input: {
			reportId: string;
			status: ReportStatus;
			overrideReason: string;
		}) =>
			parsed(
				reportSchema,
				await reportsApi.setReportStatus(
					input.reportId,
					input.status,
					input.overrideReason,
				),
				`/reports/${input.reportId}/status`,
			),
		onSuccess: () => invalidate(client, auditId),
	});
}
